'use client'
import { useTranslation } from 'react-i18next'
import type { ProductData } from '@/components/ProductData/ProductData.types'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { MAX_SCORE } from '@/features/memberReviews/memberReviews.constants'
import { useProductReviewsMetadata } from '@/features/memberReviews/ProductReviewsMetadataProvider'
import { sendDialogEvent } from '@/utils/dialogEvent'
import { useFormatter } from '@/utils/useFormatter'
import { CertifiedIcon } from 'ui/src/icons/CertifiedIcon'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { AverageRating } from './AverageRating'
import { ReviewsDisclaimer } from './ReviewsDisclaimer'
import { ReviewsDistributionDialog } from './ReviewsDistributionDialog'
import { certifiedIcon, disclaimerText, trigger } from './ProductAverageRating.css'

type Props = {
  centerAlign?: boolean
}

export const ProductAverageRating = ({ centerAlign }: Props) => {
  const { t } = useTranslation('reviews')
  const { numberGrouping } = useFormatter()
  const productData = useProductData()
  const productReviewsMetadata = useProductReviewsMetadata()

  if (!productReviewsMetadata) {
    console.log(`ProductAverageRating | No product reviews metadata found for product ${productData.name}`)
    return null
  }

  const openDialog = () => {
    // Notify that playing media should be paused as a result of the dialog being opened
    sendDialogEvent('open')
  }

  const closeDialog = () => {
    // Notify that all media paused as a result of the dialog being opened should be resumed
    sendDialogEvent('close')
  }

  const { averageRating, reviewsDistribution } = productReviewsMetadata

  const Trigger = (
    <button className={trigger} onClick={openDialog}>
      <CertifiedIcon className={certifiedIcon} color={tokens.legacyColors.textPrimary} size="1.15rem" />
      {t('REVIEWS_COUNT_LABEL', {
        count: averageRating.totalOfReviews,
        reviewsCount: numberGrouping(averageRating.totalOfReviews),
      })}
    </button>
  )

  const Header = (
    <section>
      <AverageRating as="h3" score={averageRating.score} maxScore={MAX_SCORE} />
      <ReviewsDisclaimer
        className={disclaimerText}
        size={{ _: 'xs', sm: 'md' }}
        reviewsCount={averageRating.totalOfReviews}
      />
    </section>
  )

  return (
    <>
      <script
        key="product-structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getProductStructuredData(productData, averageRating)),
        }}
      />

      <div className={sprinkles({ display: 'flex', ...(centerAlign && { justifyContent: 'center' }) })}>
        <ReviewsDistributionDialog Header={Header} reviewsDistribution={reviewsDistribution} onClose={closeDialog}>
          {Trigger}
        </ReviewsDistributionDialog>
      </div>
    </>
  )
}

type AverageRating = NonNullable<ReturnType<typeof useProductReviewsMetadata>>['averageRating']

const getProductStructuredData = (product: ProductData, averageRating: AverageRating) => {
  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.displayNameFull,
    description: product.tagline,
    brand: {
      '@type': 'Brand',
      name: 'Hedvig',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: averageRating.score,
      bestRating: MAX_SCORE,
      reviewCount: averageRating.totalOfReviews,
    },
  }
}
